import React, { useEffect, useState, useRef } from "react";
import { FileDoc, Plus, Sparkle, Trash, PencilSimple, CloudArrowUp, FloppyDisk, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { GlassCard } from "@/components/glass/GlassCard";
import { PageShell } from "@/components/layout/AppLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import SearchSortBar from "@/components/SearchSortBar";
import useSearchSort from "@/hooks/useSearchSort";

export default function Boilerplates() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newDraft, setNewDraft] = useState({ title: "", content: "" });
  const [onlyMyIndustry, setOnlyMyIndustry] = useState(true);
  const [tenantIndustry, setTenantIndustry] = useState("general");
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const params = onlyMyIndustry ? { scope: "industry" } : {};
      const { data } = await api.get("/boilerplates", { params });
      setItems(data.boilerplates || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [onlyMyIndustry]);
  useEffect(() => { api.get("/compliance/tenant").then(({ data }) => setTenantIndustry(data?.config?.industry || "general")).catch(() => {}); }, []);

  const create = async () => {
    if (!newDraft.content.trim()) return toast.error("Paste the template text");
    try {
      const { data } = await api.post("/boilerplates", newDraft);
      toast.success("Boilerplate created — running AI field detection");
      setItems([data, ...items]); setSelected(data); setNewOpen(false);
      setNewDraft({ title: "", content: "" });
      await analyzeFields(data.id);
    } catch (e) { toast.error("Create failed"); }
  };

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", file.name.replace(/\.[^.]+$/, ""));
    toast.info("Uploading and OCR-ing (if needed)…");
    try {
      const { data } = await api.post("/boilerplates/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setItems([data, ...items]); setSelected(data);
      toast.success(`Uploaded · ${data.fields?.length || 0} variable fields detected`);
    } catch (e) { toast.error("Upload failed"); }
  };

  const analyzeFields = async (id) => {
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/boilerplates/${id}/analyze-fields`);
      setItems((prev) => prev.map((b) => b.id === id ? { ...b, fields: data.fields } : b));
      if (selected?.id === id) setSelected({ ...selected, fields: data.fields });
      toast.success(`AI detected ${data.fields.length} variable fields`);
    } catch (e) { toast.error("AI field detection failed"); }
    finally { setAnalyzing(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this boilerplate?")) return;
    await api.delete(`/boilerplates/${id}`);
    setItems(items.filter((b) => b.id !== id));
    if (selected?.id === id) setSelected(null);
    toast.success("Deleted");
  };

  const save = async () => {
    if (!selected) return;
    try {
      const { data } = await api.patch(`/boilerplates/${selected.id}`, {
        title: selected.title, content: selected.content, fields: selected.fields,
      });
      toast.success("Saved");
      setItems((prev) => prev.map((b) => b.id === data.id ? data : b));
      setSelected(data);
    } catch (e) { toast.error("Save failed"); }
  };

  return (
    <PageShell
      title="Custom Boilerplates"
      subtitle="Upload your own templates. AI auto-detects the fields you'll need to fill in each time."
      right={
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setOnlyMyIndustry((v) => !v)}
            data-testid="bp-industry-filter-toggle"
            title={`Currently showing ${onlyMyIndustry ? `"${tenantIndustry}" + general` : "all industries"}`}
            className={`text-[11px] px-3 py-1.5 rounded-md border transition ${onlyMyIndustry ? "bg-[var(--surface-strong)] border-[var(--accent)] text-[var(--accent)]" : "bg-[var(--surface)] border-[var(--border)] text-[var(--text-3)] hover:text-[var(--text-2)]"}`}
          >
            {onlyMyIndustry ? `My industry · ${tenantIndustry}` : "All industries"}
          </button>
          <input ref={fileRef} type="file" className="hidden" accept=".pdf,.docx,.txt" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid="boilerplate-file-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="boilerplate-upload-btn" className="gap-2">
            <CloudArrowUp size={14} /> Upload PDF/DOCX
          </Button>
          <Button onClick={() => setNewOpen(true)} data-testid="boilerplate-new-btn" className="gap-2">
            <Plus size={14} /> New
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-4 space-y-3">
          <BoilerplateList items={items} loading={loading} selected={selected} setSelected={setSelected} />
        </div>

        <div className="lg:col-span-8">
          {selected ? (
            <GlassCard className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Input value={selected.title} onChange={(e) => setSelected({ ...selected, title: e.target.value })} data-testid="bp-title-input" className="flex-1 glass-subtle border-[var(--border)] font-semibold" />
                <Button size="sm" variant="outline" onClick={() => analyzeFields(selected.id)} disabled={analyzing} data-testid="bp-analyze-btn" className="gap-2">
                  <Sparkle size={12} weight="fill" /> {analyzing ? "Analyzing…" : "Re-detect fields"}
                </Button>
                <Button size="sm" onClick={save} data-testid="bp-save-btn" className="gap-2"><FloppyDisk size={12} /> Save</Button>
                <Button size="sm" variant="ghost" onClick={() => remove(selected.id)} data-testid="bp-delete-btn" className="text-[var(--danger)]"><Trash size={12} /></Button>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-[var(--text-2)]">Template content</Label>
                  <Textarea
                    rows={24}
                    value={selected.content}
                    onChange={(e) => setSelected({ ...selected, content: e.target.value })}
                    data-testid="bp-content-textarea"
                    className="mt-1.5 glass-subtle border-[var(--border)] font-mono text-xs"
                  />
                </div>
                <div>
                  <div className="overline mb-2 flex items-center gap-2">
                    <Sparkle size={11} weight="fill" className="text-[var(--accent)]" />
                    Editable fields ({(selected.fields || []).length})
                  </div>
                  <div className="space-y-2 max-h-[36rem] overflow-auto pe-1">
                    {(selected.fields || []).length === 0 ? (
                      <div className="text-xs text-[var(--text-3)]">No fields yet — click "Re-detect fields" to let Claude 4.5 find placeholders like <code>[DATE]</code> or <code>{"{PARTY}"}</code>.</div>
                    ) : selected.fields.map((f, i) => (
                      <div key={i} className="p-3 rounded-md border border-[var(--border)] bg-[var(--surface)]" data-testid={`bp-field-${i}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-xs font-mono bg-[var(--surface-strong)] px-1.5 py-0.5 rounded">{f.token}</span>
                          <span className="chip chip-neutral text-[10px]">{f.type || "text"}</span>
                        </div>
                        <div className="text-sm font-medium">{f.label}</div>
                        {f.section && <div className="text-[10px] text-[var(--text-3)] mt-0.5">{f.section}</div>}
                        {f.example && <div className="text-[11px] text-[var(--text-2)] mt-1 italic">e.g. "{f.example}"</div>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </GlassCard>
          ) : (
            <GlassCard className="p-10 text-center">
              <FileDoc size={36} className="mx-auto text-[var(--text-3)] mb-3" />
              <div className="text-sm text-[var(--text-2)]">Select a boilerplate on the left, or upload a new one.</div>
            </GlassCard>
          )}
        </div>
      </div>

      {newOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setNewOpen(false)}>
          <div className="w-full max-w-3xl rounded-xl bg-[var(--bg-2)] border border-[var(--border-strong)] p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-lg font-semibold tracking-tight" style={{ fontFamily: "var(--font-heading)" }}>New boilerplate</div>
              <button onClick={() => setNewOpen(false)} className="p-1.5 hover:bg-[var(--surface)] rounded"><X size={14} /></button>
            </div>
            <Input value={newDraft.title} onChange={(e) => setNewDraft({ ...newDraft, title: e.target.value })} placeholder="Template title" data-testid="bp-new-title" className="mb-3 glass-subtle border-[var(--border)]" />
            <Textarea rows={14} value={newDraft.content} onChange={(e) => setNewDraft({ ...newDraft, content: e.target.value })} placeholder="Paste your template text, using [BRACKETS] for variable fields" data-testid="bp-new-content" className="glass-subtle border-[var(--border)] font-mono text-xs" />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => setNewOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={create} data-testid="bp-new-create" className="gap-2"><Plus size={12} /> Create + detect fields</Button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}

function BoilerplateList({ items, loading, selected, setSelected }) {
  const s = useSearchSort(items, {
    searchKeys: ["title", "content"],
    sortOptions: [
      { value: "title", label: "Name A→Z", defaultDir: "asc" },
      { value: "created_at", label: "Newest", defaultDir: "desc" },
      { value: "content", label: "Length", defaultDir: "desc" },
    ],
    defaultSort: "created_at",
  });
  if (loading) return <div className="text-sm text-[var(--text-3)]">Loading…</div>;
  if (!items.length) return (
    <GlassCard className="p-6 text-center" data-testid="boilerplates-empty">
      <FileDoc size={28} className="mx-auto text-[var(--text-3)] mb-2" />
      <div className="text-sm text-[var(--text-2)]">No boilerplates yet. Upload your first template to let the AI map its editable fields.</div>
    </GlassCard>
  );
  return (
    <>
      <SearchSortBar controller={s} placeholder="Search boilerplates…" testIdPrefix="boilerplates" />
      {s.items.length === 0 ? (
        <div className="text-xs text-[var(--text-3)] py-6 text-center">No matches.</div>
      ) : s.items.map((b) => (
        <GlassCard
          key={b.id}
          hover
          as="button"
          data-testid={`boilerplate-${b.id}`}
          onClick={() => setSelected(b)}
          className={`p-4 text-left w-full ${selected?.id === b.id ? "ring-2 ring-[var(--accent)]" : ""}`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{b.title}</div>
              <div className="text-[11px] text-[var(--text-3)] mt-0.5">{(b.fields || []).length} fields · {b.content?.length || 0} chars</div>
            </div>
            <Sparkle size={14} weight="duotone" className="text-[var(--accent)] shrink-0" />
          </div>
        </GlassCard>
      ))}
    </>
  );
}
